import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit,
  Output, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef, Renderer2
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Tutorial, TutorialStep, TutorialCard, parseSharePointVideoUrl } from '../../models';
import { TutorialService } from '../../services/tutorial.service';
import { AuthService } from '#services/shared/auth.service';
import { MessageService } from 'primeng/api';

/**
 * Help Sidebar Component
 * Displays a sliding sidebar with tutorials list, search, and detail view.
 * Uses OnPush change detection for better performance.
 */
@Component({
  selector: 'app-help-sidebar',
  templateUrl: './help-sidebar.component.html',
  styleUrls: ['./help-sidebar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HelpSidebarComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() close = new EventEmitter<void>();
  @Output() tutorialSelected = new EventEmitter<Tutorial>();
  @Output() openViewer = new EventEmitter<{ tutorial: Tutorial; stepIndex: number }>();

  tutorials: Tutorial[] = [];
  filteredTutorials: Tutorial[] = [];
  searchText = '';
  isLoading = false;
  errorMessage = '';
  selectedTutorial: Tutorial | null = null;
  currentStepIndex = 0;

  /** Controls visibility of admin actions based on system admin role */
  isAdmin = false;
  isSaving = false;
  savingMessage = '';
  showFormDialog = false;
  editingTutorial: Tutorial | null = null;
  showDeleteConfirm = false;
  tutorialToDelete: Tutorial | null = null;

  // Step CRUD state
  showStepFormDialog = false;
  editingStep: TutorialStep | null = null;
  showDeleteStepConfirm = false;
  stepToDelete: TutorialStep | null = null;

  // Step reorder state
  isReorderMode = false;
  /** Working copy of steps shown in the drag-and-drop reorder panel */
  reorderStepsList: TutorialStep[] = [];

  /** Tutorial cards — placeholder data (logic to be added later) */
  tutorialCards: TutorialCard[] = [
    { label: 'Most Viewed', icon: 'pi pi-eye', active: false, action: 'most-viewed' },
    { label: 'About', icon: 'pi pi-info-circle', active: false, action: 'about' }
  ];

  /** Controls visibility of the About panel */
  showAbout = false;

  /** Subject for debounced search input */
  private searchSubject$ = new Subject<string>();

  /** Subject for unsubscribing on destroy */
  private destroy$ = new Subject<void>();

  constructor(
    private readonly tutorialService: TutorialService,
    private readonly cdr: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly authService: AuthService,
    private readonly messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.isAdmin = this.authService.isSystemAdmin();
    this.loadTutorials();
    this.setupSearch();
  }

  /**
   * Lock body scroll when sidebar opens, unlock when it closes.
   * Also reset view to tutorial list when sidebar is reopened.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      this.renderer.setStyle(document.body, 'overflow', this.isOpen ? 'hidden' : '');
      if (this.isOpen) {
        // Re-check admin status each time sidebar opens
        this.isAdmin = this.authService.isSystemAdmin();
        // Only reset search/filter, keep selectedTutorial & step for continuity
        this.searchText = '';
        this.filteredTutorials = [...this.tutorials];
        this.tutorialCards.forEach(c => c.active = false);
      }
    }
  }

  ngOnDestroy(): void {
    this.renderer.removeStyle(document.body, 'overflow');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Loads tutorials from API (cached by service) */
  loadTutorials(): void {
    this.isLoading = true;
    this.errorMessage = '';
    this.tutorialService.getAllTutorials()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tutorials) => {
          this.tutorials = tutorials
            .filter(t => t.IsActive)
            // Non-admins only see published tutorials; admins see everything
            .filter(t => this.isAdmin || t.IsPublished)
            .sort((a, b) => b.ID - a.ID);
          if (this.tutorials.length === 0) {
            this.errorMessage = 'No tutorials are available at this time. Please check back later.';
          }
          this.filteredTutorials = [...this.tutorials];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading tutorials:', error);
          this.errorMessage = 'Unable to load tutorials. Please try again later.';
          this.tutorials = [];
          this.filteredTutorials = [];
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  /** Retries loading tutorials after an error */
  retryLoad(): void {
    this.tutorialService.clearCache();
    this.loadTutorials();
  }

  /** Refreshes tutorials list to pick up any newly added tutorials */
  refreshTutorials(): void {
    if (this.isLoading) { return; }
    this.tutorialService.clearCache();
    this.loadTutorials();
  }

  /** Sets up debounced search (300ms) to avoid filtering on every keystroke */
  private setupSearch(): void {
    this.searchSubject$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchText => {
      this.filterTutorials(searchText);
      this.cdr.markForCheck();
    });
  }

  /** Emits search text to debounced subject */
  onSearch(): void {
    this.searchSubject$.next(this.searchText);
  }

  /** Clears the search input and resets the filtered list */
  clearSearch(): void {
    this.searchText = '';
    this.searchSubject$.next('');
  }

  /** Filters tutorials by title or description */
  private filterTutorials(searchText: string): void {
    const trimmed = searchText.trim();
    if (!trimmed) {
      this.filteredTutorials = [...this.tutorials];
      return;
    }

    const searchLower = trimmed.toLowerCase();
    this.filteredTutorials = this.tutorials.filter(tutorial =>
      tutorial.Title.toLowerCase().includes(searchLower) ||
      tutorial.Description.toLowerCase().includes(searchLower)
    );
  }

  /** Selects a tutorial and navigates to its detail view */
  onTutorialClick(tutorial: Tutorial): void {
    this.selectedTutorial = tutorial;
    this.currentStepIndex = 0;
    this.tutorialSelected.emit(tutorial);
  }

  /** Emits close event to parent */
  onClose(): void {
    this.close.emit();
  }

  /** True when any admin create/edit/delete dialog is currently open */
  get isAnyDialogOpen(): boolean {
    return this.showFormDialog
      || this.showStepFormDialog
      || this.showDeleteConfirm
      || this.showDeleteStepConfirm;
  }

  /** Closes sidebar when overlay is clicked — ignored while a dialog is open */
  onOverlayClick(): void {
    if (this.isAnyDialogOpen) { return; }
    this.close.emit();
  }

  /** Navigates back to the tutorials list */
  backToList(): void {
    this.selectedTutorial = null;
    this.currentStepIndex = 0;
    this.isReorderMode = false;
    this.reorderStepsList = [];
    this.cdr.markForCheck();
  }

  /** Returns the full image URL for a step using the correct API endpoint */
  getImageUrl(step: TutorialStep): string {
    return this.tutorialService.getStepImageUrl(step);
  }

  /** Returns the current step object in the sidebar detail view */
  get currentSidebarStep(): TutorialStep | null {
    return this.selectedTutorial?.Steps?.[this.currentStepIndex] ?? null;
  }

  /** Progress percentage for the sidebar step progress bar */
  get sidebarProgressPercent(): number {
    if (!this.selectedTutorial?.Steps?.length) { return 0; }
    return ((this.currentStepIndex + 1) / this.selectedTutorial.Steps.length) * 100;
  }

  /** Navigate to the next step in the sidebar */
  nextSidebarStep(): void {
    if (this.selectedTutorial && this.currentStepIndex < this.selectedTutorial.Steps.length - 1) {
      this.currentStepIndex++;
    }
  }

  /** Navigate to the previous step in the sidebar */
  prevSidebarStep(): void {
    if (this.currentStepIndex > 0) {
      this.currentStepIndex--;
    }
  }

  /** Jump to a specific step in the sidebar */
  goToSidebarStep(index: number): void {
    if (this.selectedTutorial && index >= 0 && index < this.selectedTutorial.Steps.length) {
      this.currentStepIndex = index;
    }
  }

  /** Opens the full-screen zoomed step viewer at the given step index */
  openZoomedView(stepIndex: number): void {
    if (this.selectedTutorial) {
      this.currentStepIndex = stepIndex;
      this.openViewer.emit({ tutorial: this.selectedTutorial, stepIndex });
    }
  }

  /** Called when the zoomed viewer closes — resumes sidebar at the last viewed step */
  resumeFromStep(stepIndex: number): void {
    this.currentStepIndex = stepIndex;
    this.cdr.markForCheck();
  }

  /** Selects a tutorial and jumps to a specific step (used for FAB resume) */
  selectTutorialAndStep(tutorial: Tutorial, stepIndex: number): void {
    this.selectedTutorial = tutorial;
    this.currentStepIndex = stepIndex;
    this.cdr.markForCheck();
  }

  getVideoHref(tutorial: Tutorial): string | null {
    return parseSharePointVideoUrl(tutorial.VideoLink)?.href ?? null;
  }

  /** Lets the native link open the video; blocks and warns when the link is invalid */
  onVideoClick(tutorial: Tutorial, event: Event): void {
    event.stopPropagation();
    if (this.getVideoHref(tutorial)) { return; }
    event.preventDefault();
    this.messageService.add({
      severity: 'warn',
      summary: 'Video unavailable',
      detail: 'This tutorial\'s video link is not a valid SharePoint link (https://...sharepoint.com/...).',
      life: 5000
    });
  }

  // ── Admin CRUD actions ──────────────────────────────────────────

  /** Opens the create tutorial dialog */
  onCreateTutorial(): void {
    this.messageService.clear();
    this.editingTutorial = null;
    this.showFormDialog = true;
  }

  /** Opens the edit dialog for a tutorial (from list view) */
  onEditTutorial(tutorial: Tutorial, event: Event): void {
    event.stopPropagation();
    this.messageService.clear();
    this.editingTutorial = tutorial;
    this.showFormDialog = true;
  }

  /** Opens the delete confirmation for a tutorial (from list view) */
  onDeleteTutorial(tutorial: Tutorial, event: Event): void {
    event.stopPropagation();
    this.tutorialToDelete = tutorial;
    this.showDeleteConfirm = true;
  }

  /** Opens the edit dialog for the currently selected tutorial (detail view) */
  onEditCurrentTutorial(): void {
    if (this.selectedTutorial) {
      this.messageService.clear();
      this.editingTutorial = this.selectedTutorial;
      this.showFormDialog = true;
    }
  }

  /** Opens delete confirmation for the currently selected tutorial (detail view) */
  onDeleteCurrentTutorial(): void {
    if (this.selectedTutorial) {
      this.tutorialToDelete = this.selectedTutorial;
      this.showDeleteConfirm = true;
    }
  }

  /** Handles save from the tutorial form dialog (create or update) */
  onSaveTutorial(data: Record<string, any>): void {
    this.isSaving = true;
    this.savingMessage = this.editingTutorial ? 'Updating tutorial...' : 'Creating tutorial...';
    this.cdr.markForCheck();
    if (this.editingTutorial) {
      this.tutorialService.updateTutorial(this.editingTutorial.ID, data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            if (this.selectedTutorial?.ID === this.editingTutorial?.ID) {
              this.selectedTutorial = { ...this.selectedTutorial!, ...data } as Tutorial;
            }
            this.showFormDialog = false;
            this.isSaving = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tutorial updated successfully.', life: 4000 });
            this.refreshTutorials();
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error updating tutorial:', err);
            this.isSaving = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update tutorial.', life: 5000 });
            this.cdr.markForCheck();
          }
        });
    } else {
      this.tutorialService.createTutorial(data)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showFormDialog = false;
            this.isSaving = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tutorial created successfully.', life: 4000 });
            this.refreshTutorials();
            this.cdr.markForCheck();
          },
          error: (err) => {
            console.error('Error creating tutorial:', err);
            this.isSaving = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to create tutorial.', life: 5000 });
            this.cdr.markForCheck();
          }
        });
    }
  }

  /** Confirms and executes deletion of the selected tutorial */
  confirmDelete(): void {
    if (!this.tutorialToDelete) { return; }
    this.isSaving = true;
    this.savingMessage = 'Deleting tutorial...';
    this.cdr.markForCheck();
    this.tutorialService.deleteTutorial(this.tutorialToDelete.ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.selectedTutorial?.ID === this.tutorialToDelete?.ID) {
            this.selectedTutorial = null;
            this.currentStepIndex = 0;
          }
          this.showDeleteConfirm = false;
          this.tutorialToDelete = null;
          this.isSaving = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Tutorial deleted successfully.', life: 4000 });
          this.refreshTutorials();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error deleting tutorial:', err);
          this.showDeleteConfirm = false;
          this.tutorialToDelete = null;
          this.isSaving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete tutorial.', life: 5000 });
          this.cdr.markForCheck();
        }
      });
  }

  /** Cancels the delete confirmation */
  cancelDelete(): void {
    this.showDeleteConfirm = false;
    this.tutorialToDelete = null;
  }

  /**
   * Toggles a tutorial's published visibility (admin-only).
   * Published tutorials are visible to all viewers; unpublished stay admin-only.
   */
  onToggleVisibility(tutorial: Tutorial, event: Event): void {
    event.stopPropagation();
    const newState = !tutorial.IsPublished;
    this.isSaving = true;
    this.savingMessage = newState ? 'Publishing tutorial...' : 'Unpublishing tutorial...';
    this.cdr.markForCheck();
    this.tutorialService.toggleTutorialVisibility(tutorial.ID, newState)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          tutorial.IsPublished = newState;
          if (this.selectedTutorial?.ID === tutorial.ID) {
            this.selectedTutorial = { ...this.selectedTutorial, IsPublished: newState } as Tutorial;
          }
          this.isSaving = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: newState ? 'Tutorial published successfully.' : 'Tutorial unpublished successfully.',
            life: 4000
          });
          this.refreshTutorials();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error toggling tutorial visibility:', err);
          this.isSaving = false;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update tutorial visibility.',
            life: 5000
          });
          this.cdr.markForCheck();
        }
      });
  }

  // ── Step CRUD actions ──────────────────────────────────────

  /** Opens the add step dialog */
  onAddStep(): void {
    this.messageService.clear();
    this.editingStep = null;
    this.showStepFormDialog = true;
  }

  /** Opens edit dialog for a step — fetches full step detail to ensure ImageUrl is populated */
  onEditStep(step: TutorialStep, event: Event): void {
    event.stopPropagation();
    this.messageService.clear();
    this.tutorialService.getStepById(step.TutorialID, step.ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fullStep) => {
          this.editingStep = fullStep;
          this.showStepFormDialog = true;
          this.cdr.markForCheck();
        },
        error: () => {
          this.editingStep = step; // fallback to list data
          this.showStepFormDialog = true;
          this.cdr.markForCheck();
        }
      });
  }

  /** Opens delete confirmation for a step */
  onDeleteStep(step: TutorialStep, event: Event): void {
    event.stopPropagation();
    this.stepToDelete = step;
    this.showDeleteStepConfirm = true;
  }

  /** Next step number for auto-incrementing */
  get nextStepNumber(): number {
    if (!this.selectedTutorial?.Steps?.length) { return 1; }
    return Math.max(...this.selectedTutorial.Steps.map(s => s.StepNumber)) + 1;
  }

  /** Handles save from step form dialog (add or update) */
  onSaveStep(event: { step: Record<string, any>; imageFile: File | null }): void {
    if (!this.selectedTutorial) { return; }
    const tutorialId = this.selectedTutorial.ID;
    const { step: data, imageFile } = event;
    this.isSaving = true;
    this.savingMessage = this.editingStep ? 'Updating step...' : 'Adding step...';
    this.cdr.markForCheck();

    if (this.editingStep) {
      this.tutorialService.updateStep(tutorialId, this.editingStep.ID, data, imageFile)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showStepFormDialog = false;
            this.isSaving = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Step updated successfully.', life: 4000 });
            this.reloadSelectedTutorial(tutorialId);
          },
          error: (err) => {
            console.error('Error updating step:', err);
            this.isSaving = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update step.', life: 5000 });
            this.cdr.markForCheck();
          }
        });
    } else {
      this.tutorialService.addStepToTutorial(tutorialId, data, imageFile)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.showStepFormDialog = false;
            this.isSaving = false;
            this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Step added successfully.', life: 4000 });
            this.reloadSelectedTutorial(tutorialId, true);
          },
          error: (err) => {
            console.error('Error adding step:', err);
            this.isSaving = false;
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add step.', life: 5000 });
            this.cdr.markForCheck();
          }
        });
    }
  }

  /** Confirms and deletes a step */
  confirmDeleteStep(): void {
    if (!this.stepToDelete || !this.selectedTutorial) { return; }
    const tutorialId = this.selectedTutorial.ID;
    this.isSaving = true;
    this.savingMessage = 'Deleting step...';
    this.cdr.markForCheck();
    this.tutorialService.deleteStep(tutorialId, this.stepToDelete.ID)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showDeleteStepConfirm = false;
          this.stepToDelete = null;
          this.currentStepIndex = 0;
          this.isSaving = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Step deleted successfully.', life: 4000 });
          this.reloadSelectedTutorial(tutorialId);
        },
        error: (err) => {
          console.error('Error deleting step:', err);
          this.showDeleteStepConfirm = false;
          this.stepToDelete = null;
          this.isSaving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to delete step.', life: 5000 });
          this.cdr.markForCheck();
        }
      });
  }

  /** Cancels the step delete confirmation */
  cancelDeleteStep(): void {
    this.showDeleteStepConfirm = false;
    this.stepToDelete = null;
  }

  // ── Step reorder actions ───────────────────────────────────

  /** True when the selected tutorial has enough steps to reorder */
  get canReorderSteps(): boolean {
    return (this.selectedTutorial?.Steps?.length ?? 0) > 1;
  }

  /** Enters drag-and-drop reorder mode with a working copy of the current steps */
  enterReorderMode(): void {
    if (!this.canReorderSteps) { return; }
    this.reorderStepsList = [...(this.selectedTutorial!.Steps ?? [])];
    this.isReorderMode = true;
    this.cdr.markForCheck();
  }

  /** Exits reorder mode without persisting any changes */
  cancelReorder(): void {
    this.isReorderMode = false;
    this.reorderStepsList = [];
    this.cdr.markForCheck();
  }

  /** Moves a step within the working list when a drag operation completes */
  onStepDrop(event: CdkDragDrop<TutorialStep[]>): void {
    moveItemInArray(this.reorderStepsList, event.previousIndex, event.currentIndex);
    this.cdr.markForCheck();
  }

  /** True when the working list order differs from the persisted step order */
  get hasReorderChanges(): boolean {
    const original = this.selectedTutorial?.Steps ?? [];
    if (original.length !== this.reorderStepsList.length) { return false; }
    return this.reorderStepsList.some((step, i) => step.ID !== original[i].ID);
  }

  /** Persists the new step order, then reflects it immediately in the viewer */
  saveStepOrder(): void {
    if (!this.selectedTutorial || !this.hasReorderChanges) {
      this.cancelReorder();
      return;
    }
    const tutorialId = this.selectedTutorial.ID;
    const orderedIds = this.reorderStepsList.map(s => s.ID);
    const currentStepId = this.currentSidebarStep?.ID ?? null;
    this.isSaving = true;
    this.savingMessage = 'Saving step order...';
    this.cdr.markForCheck();

    this.tutorialService.reorderSteps(tutorialId, orderedIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          // Reflect the new order immediately without waiting for a reload
          const reordered = this.reorderStepsList.map((step, i) => ({ ...step, OrderIndex: i + 1 }));
          this.selectedTutorial = { ...this.selectedTutorial!, Steps: reordered } as Tutorial;
          const newIndex = currentStepId != null ? reordered.findIndex(s => s.ID === currentStepId) : 0;
          this.currentStepIndex = newIndex >= 0 ? newIndex : 0;
          this.isReorderMode = false;
          this.reorderStepsList = [];
          this.isSaving = false;
          this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Step order updated successfully.', life: 4000 });
          this.tutorialService.clearCache();
          this.loadTutorials();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error reordering steps:', err);
          this.isSaving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update step order.', life: 5000 });
          this.cdr.markForCheck();
        }
      });
  }

  /** Reloads the selected tutorial from the API to refresh steps */
  private reloadSelectedTutorial(tutorialId: number, navigateToLast = false): void {
    this.tutorialService.clearCache();
    this.tutorialService.getTutorialById(tutorialId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (tutorial) => {
          this.selectedTutorial = tutorial;
          if (navigateToLast) {
            this.currentStepIndex = Math.max(0, (tutorial.Steps?.length || 1) - 1);
          } else if (this.currentStepIndex >= (tutorial.Steps?.length || 0)) {
            this.currentStepIndex = Math.max(0, (tutorial.Steps?.length || 1) - 1);
          }
          this.loadTutorials();
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Error reloading tutorial:', err);
          this.cdr.markForCheck();
        }
      });
  }

  /** Handles tutorial card click — filters/sorts the tutorial list based on chip action */
  onCardClick(card: TutorialCard): void {
    const wasActive = card.active;
    this.tutorialCards.forEach(c => c.active = false);
    card.active = !wasActive;

    if (card.active && card.action) {
      this.applyCardAction(card.action);
    } else {
      // Deactivated — restore default sort (by ID descending)
      this.showAbout = false;
      this.filteredTutorials = [...this.tutorials];
      this.cdr.markForCheck();
    }
  }

  /** Applies the sort/filter action associated with a chip */
  private applyCardAction(action: string): void {
    this.showAbout = false;
    switch (action) {
      case 'most-viewed':
        this.filteredTutorials = [...this.tutorials].sort((a, b) => (b.NoOfViews || 0) - (a.NoOfViews || 0));
        break;
      case 'about':
        this.showAbout = true;
        break;
      default:
        this.filteredTutorials = [...this.tutorials];
        break;
    }
    this.cdr.markForCheck();
  }

  /** TrackBy function for tutorial cards */
  trackByCardLabel(index: number, card: TutorialCard): string {
    return card.label;
  }

  /** TrackBy function for tutorial list to optimize *ngFor rendering */
  trackByTutorialId(index: number, tutorial: Tutorial): number {
    return tutorial.ID;
  }

  /** TrackBy function for steps list to optimize *ngFor rendering */
  trackByStepId(index: number, step: TutorialStep): number {
    return step.ID;
  }
}
